// src/lib/menuDesign.js
// Système de design du menu : TOUT passe par des "tokens" (couleurs, rayon, style de carte).
// Le rendu ne dépend que de ces tokens → ajouter un design = ajouter un preset (ou des couleurs custom).
// Compatible avec les anciens réglages (theme / customColors / cardColor…) : ils sont convertis en tokens.

import { resolveTheme, getBgStyle } from './themeConfig'

export const PRESETS = {
  'dar-zayna': {
    name: 'Riad',
    description: 'Crème, vert forêt et orange',
    tokens: { bg: '#FBF8F2', surface: '#FFFFFF', text: '#1F2A22', muted: '#6B6F68', accent: '#1F4D36', price: '#C8501E', badge: '#E8672C', chip: '#F1ECE2', border: 'rgba(31,42,34,.08)', radius: 16 },
  },
  'noir-or': {
    name: 'Noir & or',
    description: 'Sombre, luxueux',
    tokens: { bg: '#121212', surface: '#1E1E1E', text: '#F8F2E6', muted: '#A79F8F', accent: '#D6B56D', price: '#D6B56D', badge: '#D6B56D', chip: '#262626', border: 'rgba(214,181,109,.22)', radius: 14 },
  },
  ocean: {
    name: 'Océan',
    description: 'Bleu clair, frais et moderne',
    tokens: { bg: '#F3F8FB', surface: '#FFFFFF', text: '#0F2A3D', muted: '#5C7488', accent: '#0E7490', price: '#0E7490', badge: '#F59E0B', chip: '#E3EEF4', border: 'rgba(15,42,61,.08)', radius: 18 },
  },
  rubis: {
    name: 'Rubis',
    description: 'Blanc rosé et rouge profond',
    tokens: { bg: '#FFF8F6', surface: '#FFFFFF', text: '#3A1F22', muted: '#7A5A5E', accent: '#B4233C', price: '#B4233C', badge: '#F59E0B', chip: '#F9E9E6', border: 'rgba(58,31,34,.08)', radius: 14 },
  },
  graphite: {
    name: 'Graphite',
    description: 'Sobre, ardoise et menthe',
    tokens: { bg: '#0F172A', surface: '#1E293B', text: '#F1F5F9', muted: '#94A3B8', accent: '#2DD4BF', price: '#2DD4BF', badge: '#FBBF24', chip: '#1E293B', border: 'rgba(148,163,184,.18)', radius: 12 },
  },
}

export const PRESET_LIST = Object.entries(PRESETS).map(([id, p]) => ({ id, name: p.name, description: p.description, tokens: p.tokens }))

export const ITEM_LAYOUTS = [
  { id: 'list', name: 'Liste', description: 'Photo à gauche' },
  { id: 'grid', name: 'Grille', description: '2 colonnes, grandes photos' },
  { id: 'compact', name: 'Compact', description: 'Sans photo, dense' },
]

const isHex = (v) => /^#[0-9a-f]{6}$/i.test(v || '')

export function luminance(hex) {
  if (!isHex(hex)) return 0.5
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

// Couleur de texte lisible sur une couleur donnée
export const onColor = (hex) => (isHex(hex) && luminance(hex) > 0.4 ? '#111111' : '#FFFFFF')

function legacyTokens(settings) {
  const theme = resolveTheme(settings)
  const bgStyle = getBgStyle(theme)
  const bg = bgStyle.backgroundColor || (isHex(theme.bg) ? theme.bg : '#FFFFFF')
  const dark = luminance(theme.text) > 0.5
  return {
    bg,
    pageBackground: bgStyle.background || bgStyle.backgroundColor || bg,
    surface: dark ? 'rgba(255,255,255,.07)' : '#FFFFFF',
    text: theme.text,
    muted: `color-mix(in srgb, ${theme.text} 62%, transparent)`,
    accent: theme.accent,
    price: theme.accent,
    badge: theme.accent,
    chip: dark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.05)',
    border: dark ? 'rgba(255,255,255,.12)' : 'rgba(0,0,0,.06)',
    radius: 16,
  }
}

export function resolveDesign(settings = {}) {
  const preset = PRESETS[settings.designPreset]
  const t = { ...(preset ? preset.tokens : legacyTokens(settings)) }

  // Couleurs personnalisées (éditeur) : surchargent le preset
  if (settings.customColors) {
    if (isHex(settings.customBg)) { t.bg = settings.customBg; t.pageBackground = undefined }
    if (isHex(settings.customAccent)) { t.accent = settings.customAccent; t.price = settings.customAccent }
    if (isHex(settings.customText)) { t.text = settings.customText; t.muted = `color-mix(in srgb, ${settings.customText} 62%, transparent)` }
  }
  if (settings.cardColorEnabled) {
    if (isHex(settings.cardColor)) t.surface = settings.cardColor
    if (isHex(settings.cardBorderColor)) t.border = settings.cardBorderColor
  }

  const dark = luminance(t.bg) < 0.4
  return {
    ...t,
    pageBackground: t.pageBackground || t.bg,
    accentText: onColor(t.accent),
    badgeText: onColor(t.badge || t.accent),
    dark,
    shadow: dark ? '0 1px 0 rgba(255,255,255,.04)' : '0 2px 12px rgba(30,40,30,.07)',
  }
}

// Tokens → variables CSS (utilisées partout dans le rendu)
export function designVars(d) {
  return {
    '--mr-bg': d.bg,
    '--mr-surface': d.surface,
    '--mr-text': d.text,
    '--mr-muted': d.muted,
    '--mr-accent': d.accent,
    '--mr-accent-text': d.accentText,
    '--mr-price': d.price,
    '--mr-badge': d.badge,
    '--mr-badge-text': d.badgeText,
    '--mr-chip': d.chip,
    '--mr-border': d.border,
    '--mr-radius': `${d.radius}px`,
    '--mr-shadow': d.shadow,
  }
}